import React, { createContext, useContext, useState } from 'react';
import { Menu } from '@mantine/core';
import { IconPin, IconPinFilled, IconSettings } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import type { GroupLookup } from '@/types/group.type';
import groupsService from '@/services/groups.service';
import { useGlobalSettings, useNotification } from '@/hooks';
import { useQueryClient } from '@tanstack/react-query';
import { QUERY_KEYS } from '@/constants';
import { useTranslation } from 'react-i18next';

// Global context for managing context menu state
type GroupContextMenuState = {
  opened: boolean;
  position: { x: number; y: number };
  groupData: GroupLookup | null;
  isPinnable: boolean;
  isPinningInProgress: boolean;
  openMenu: (groupData: GroupLookup, isPinnable: boolean, x: number, y: number) => void;
  closeMenu: () => void;
};

const GroupContextMenuContext = createContext<GroupContextMenuState | undefined>(undefined);

// eslint-disable-next-line react-refresh/only-export-components
export const useGroupContextMenu = () => {
  const context = useContext(GroupContextMenuContext);

  if (!context) {
    throw new Error('useGroupContextMenu must be used within GroupContextMenuProvider');
  }
  return context;
};

export const GroupContextMenuProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();

  const { currentChurch } = useGlobalSettings();
  const { showErrorNotification } = useNotification();

  const queryClient = useQueryClient();

  const { t } = useTranslation('global');

  const [opened, setOpened] = useState<boolean>(false);
  const [position, setPosition] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [groupData, setGroupData] = useState<GroupLookup | null>(null);
  const [isPinnable, setIsPinnable] = useState<boolean>(false);
  const [isPinningInProgress, setIsPinningInProgress] = useState<boolean>(false);

  const openMenu = (newGroupData: GroupLookup, newIsPinnable: boolean, x: number, y: number) => {
    setGroupData(newGroupData);
    setIsPinnable(newIsPinnable);
    setPosition({ x, y });
    setOpened(true);
  };

  const closeMenu = () => {
    setOpened(false);
    setGroupData(null);
    setIsPinnable(false);
  };

  const handlePinClick = async () => {
    try {
      if (groupData) {
        setIsPinningInProgress(true);
        await groupsService.toggleGroupPinned(currentChurch?.churchId || '', groupData.id, !groupData.isPinned);
        await queryClient.invalidateQueries({
          queryKey: [`${QUERY_KEYS.GROUPS}-${currentChurch?.churchId}`],
        });
      }
    } catch (error: unknown) {
      showErrorNotification(error);
    } finally {
      closeMenu();
      setIsPinningInProgress(false);
    }
  };

  const handleSettingsClick = () => {
    if (groupData) {
      navigate(`/groups/${groupData.id}/settings`);
    }
    closeMenu();
  };

  const contextValue: GroupContextMenuState = {
    opened,
    position,
    groupData,
    isPinningInProgress,
    isPinnable,
    openMenu,
    closeMenu,
  };

  return (
    <GroupContextMenuContext.Provider value={contextValue}>
      {children}

      <Menu opened={opened} onChange={closeMenu} position="bottom-start" shadow="md" width={200}>
        <Menu.Target>
          <div
            style={{
              position: 'fixed',
              left: position.x,
              top: position.y,
              width: 1,
              height: 1,
              pointerEvents: 'none',
            }}
          />
        </Menu.Target>

        <Menu.Dropdown>
          {groupData && (
            <>
              {/* Pin/Unpin - for groups only */}
              {isPinnable && (
                <Menu.Item
                  leftSection={groupData.isPinned ? <IconPinFilled size={16} /> : <IconPin size={16} />}
                  onClick={handlePinClick}
                >
                  {groupData.isPinned ? t('unpinFromTheTop') : t('pinToTheTop')}
                </Menu.Item>
              )}

              {/* Settings - for groups and subgroups */}
              <Menu.Item leftSection={<IconSettings size={16} />} onClick={handleSettingsClick}>
                {t('groupSettings')}
              </Menu.Item>
            </>
          )}
        </Menu.Dropdown>
      </Menu>
    </GroupContextMenuContext.Provider>
  );
};
